import axios from 'axios'
import AuthService from '../services/AuthService'

const REFERRALS_POOL_BASE = 'referrals/referrals-pool'

function isFeasibilityEnabled(value) {
    return value === '1' || value === true
}

function referralsPoolPath(useFeasibility) {
    return useFeasibility ? `${REFERRALS_POOL_BASE}/feasibility` : REFERRALS_POOL_BASE
}

const ReferralsPoolAPI = {
    create(data, options = {}) {
        return axios.post(referralsPoolPath(isFeasibilityEnabled(options.feasibility)), data)
    },

    getAvailableLocations(userId, query) {
        const _fields = ['id', 'name', 'address', 'city', 'state', 'zip', 'isAvailabilitySet', 'distanceToPatient'].join(',')
        const params = {
            _fields,
            ...query,
        }
        return axios.get(`users/${userId}/matching-locations`, { params })
    },

    getAncillaryAvailableLocations(practiceId, query) {
        const _fields = ['id', 'name', 'address', 'city', 'state', 'zip', 'isAvailabilitySet', 'distanceToPatient'].join(',')
        const params = {
            _fields,
            ...query,
        }
        return axios.get(`practices/${practiceId}/matching-locations`, { params })
    },

    getReferralsPool(type, id, query = {}) {
        const params = { ...query }
        if (AuthService.isPracticeAncillary()) {
            params[type === 'received' ? 'toPracticeId' : 'fromPracticeId'] = id
        } else {
            params[type === 'received' ? 'toUserId' : 'fromUserId'] = id
        }
        const useFeasibility = type === 'made' && isFeasibilityEnabled(params.feasibility)
        return axios.get(referralsPoolPath(useFeasibility), { params })
    },

    getReferralsPoolProviders(referralPoolId, options = {}) {
        const useFeasibility = isFeasibilityEnabled(options.feasibility)

        // Deduplicate: if same pool+feasibility request is already in flight, return that promise (avoids double 15s calls)
        const cacheKey = `${referralPoolId}:${useFeasibility}`
        if (!ReferralsPoolAPI._providersInFlight) ReferralsPoolAPI._providersInFlight = {}
        const existing = ReferralsPoolAPI._providersInFlight[cacheKey]
        if (existing) return existing

        // Feasibility popup: lighter fields to avoid 15–20s response (isSignupFinished, isInMyNetwork, etc. trigger many DB fetches)
        const _fields = useFeasibility
            ? [
                  'id',
                  'email',
                  'phone',
                  'firstName',
                  'lastName',
                  'designation',
                  'userRole(id,name)',
                  'specialities(speciality(id,name),subSpeciality(id,name))',
                  'professionalRating',
                  'connectionCount',
                  'practice(id,name,practiceType,locations(id,name,address,city,state,zip,latitude,longitude))',
                  'avatar(thumb-360,source)',
                  'physicianGroup(id,name)',
                  'name',
                  'image',
              ].join(',')
            : [
                  'id',
                  'email',
                  'phone',
                  'firstName',
                  'lastName',
                  'designation',
                  'userRole(id,name)',
                  'specialities(speciality(id,name),subSpeciality(id,name))',
                  'professionalRating',
                  'rating',
                  'connectionCount',
                  'referralsReceivedCount',
                  'isFavorited',
                  'isInMyNetwork',
                  'isSignupFinished',
                  'practice(id,name,practiceType,locations(id,name,address,city,state,zip,latitude,longitude))',
                  'avatar(thumb-360,source)',
                  'physicianGroup(id,name)',
                  'name',
                  'image',
              ].join(',')

        const base = `${REFERRALS_POOL_BASE}/${referralPoolId}`
        const listPath = `${base}/${useFeasibility ? 'feasibility-' : ''}providers-list`
        const promise = axios.get(listPath, { params: { _fields } })
        ReferralsPoolAPI._providersInFlight[cacheKey] = promise
        promise.finally(() => delete ReferralsPoolAPI._providersInFlight[cacheKey])
        return promise
    },

    getReferralPoolById(id, type, activeUserId, practiceId) {
        const params = { type, activeUserId, practiceId }
        return axios.get(`${REFERRALS_POOL_BASE}/${id}`, { params })
    },

    promoteConstrained(referralPoolId) {
        return axios.post(`${REFERRALS_POOL_BASE}/${referralPoolId}/promote-constrained`)
    },

    sendProposeTimeSlots(referralPoolId, data) {
        return axios.put(`${REFERRALS_POOL_BASE}/${referralPoolId}/propose-time-slots`, data)
    },

    accessWithToken(npiNumber, token, email) {
        const payload = { token }
        if (npiNumber) payload.npiNumber = npiNumber
        if (email) payload.email = email
        return axios.post('unauthenticated/referrals-pool/external/access-with-token', payload)
    },

    verifyWithToken(token, externalProviderReferralsAccessLogId) {
        const _fields = [
            'id',
            'fromUser(id,firstName,lastName,designation,avatar(source,thumb-360))',
            'referralNotes(id,fileName,note,url,size,extension)',
            'referralData(diagnosis,timeframe,patient(firstName,lastName))',
            'expiresAt',
            'createdAt',
        ].join(',')

        const params = { _fields }

        return axios.post(
            'unauthenticated/referrals-pool/external/verify-email-with-token',
            {
                token,
                externalProviderReferralsAccessLogId,
            },
            { params },
        )
    },

    externalProposeTimeSlots(referralPoolId, data) {
        return axios.post(`unauthenticated/referrals-pool/external/propose-time-slots`, data)
    },
    decline(referralPoolId, data) {
        return axios.post(`${REFERRALS_POOL_BASE}/${referralPoolId}/decline`, data)
    },
    additionalRecordRequest(referralPoolId, data) {
        return axios.post(`/referrals/referrals-pool/${referralPoolId}/additional-records-request`, data)
    },
    uploadAdditionalRecordRequest(requestId, data) {
        return axios.post(`/referrals/additional-records-request/${requestId}/upload`, data)
    },
}

export default ReferralsPoolAPI
